import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { createContext, useContext, useMemo } from 'react'

const DEFAULT_STALE_TIME = 5 * 60 * 1000

interface Options<T> {
  queryKeyPrefix: string
  fetcher: (ids: readonly string[]) => Promise<readonly T[]>
  getId: (item: T) => string
  staleTime?: number
}

export function createByIdsCache<T>({
  queryKeyPrefix,
  fetcher,
  getId,
  staleTime = DEFAULT_STALE_TIME,
}: Options<T>) {
  const Context = createContext<Record<string, T> | null>(null)
  const EMPTY_MAP: Record<string, T> = {}

  function useByIds(ids: readonly string[]) {
    const sortedIds = useMemo(() => Array.from(new Set(ids)).sort(), [ids])
    return useQuery({
      queryKey: [queryKeyPrefix, sortedIds],
      queryFn: async (): Promise<Record<string, T>> => {
        if (sortedIds.length === 0) return EMPTY_MAP
        const items = await fetcher(sortedIds)
        const map: Record<string, T> = {}
        for (const item of items) {
          map[getId(item)] = item
        }
        return map
      },
      staleTime,
      enabled: sortedIds.length > 0,
    })
  }

  function useCache() {
    return useContext(Context)
  }

  function Provider({
    ids,
    children,
  }: {
    ids: readonly string[]
    children: ReactNode
  }) {
    const { data } = useByIds(ids)
    return (
      <Context.Provider value={data ?? EMPTY_MAP}>{children}</Context.Provider>
    )
  }

  return { useByIds, useCache, Provider }
}
